<template>
	<div>
		<v-row class="mt-2 mx-1">
			<v-col cols="12" md="9">
				<h2 class="text-left mb-1">Compare Transactions</h2>
			</v-col>
			<v-col cols="12" md="3" v-if="currentUser.privileges.canExportFinanceTransactionReports">
				<div class="text-right mb-1">
					<download-excel
						:fetch="getExportData"
						:fields="getExportFields()"
						name="CompareTransactions"
						v-if="period1.startDate && period1.endDate && period2.startDate && period2.endDate && !isLoading"
					>
						<v-btn
							class="mx-2"
							:loading="export_in_progress"
							:disabled="export_in_progress"
							color="white"
							elevation="1"
						>
							Export
							<v-icon right dark> mdi-export </v-icon>
							<template v-slot:loader>
								<span class="custom-loader">
								<v-icon light>mdi-cached</v-icon>
								</span>
							</template>
						</v-btn>
					</download-excel>
				</div>
			</v-col>
		</v-row>

		<v-row id="compare-row">
			<!-- Period 1 Dates -->
			<v-col cols="12" md="6">
				<h3 class="mx-2 font-weight-medium text-left mb-2">Period 1</h3>
				<v-row>
					<!-- period 1 start date -->
					<v-col cols="12" md="6" sm="6">
						<v-menu
							v-model="period1.menu1"
							:close-on-content-click="false"
							:nudge-right="40"
							transition="scale-transition"
							offset-y
							min-width="auto"
						>
							<template v-slot:activator="{ on, attrs }">
								<v-text-field
									:value="formattedDate(period1.startDate)"
									label="Start Date"
									outlined
									dense
									class="rounded-input"
									readonly
									v-bind="attrs"
									v-on="on"
								></v-text-field>
							</template>
							<v-date-picker
								v-model="period1.startDate"
								@input="period1.menu1 = false"
							></v-date-picker>
						</v-menu>
					</v-col>

					<!-- period 1 end date -->
					<v-col cols="12" md="6" sm="6">
						<v-menu
							v-model="period1.menu2"
							:close-on-content-click="false"
							:nudge-right="40"
							transition="scale-transition"
							offset-y
							min-width="auto"
						>
							<template v-slot:activator="{ on, attrs }">
								<v-text-field
									:value="formattedDate(period1.endDate)"
									label="End Date"
									outlined
									dense
									class="rounded-input"
									readonly
									v-bind="attrs"
									v-on="on"
								></v-text-field>
							</template>
							<v-date-picker
								v-model="period1.endDate"
								@input="period1.menu2 = false"
							></v-date-picker>
						</v-menu>
					</v-col>
				</v-row>
			</v-col>

			<!-- Period 2 dates -->
			<v-col cols="12" md="6">
				<h3 class="mx-2 font-weight-medium text-left mb-2">Period 2</h3>
				<v-row>
					<!-- period 2 start date -->
					<v-col cols="12" md="6" sm="6">
						<v-menu
							v-model="period2.menu1"
							:close-on-content-click="false"
							:nudge-right="40"
							transition="scale-transition"
							offset-y
							min-width="auto"
						>
							<template v-slot:activator="{ on, attrs }">
								<v-text-field
									:value="formattedDate(period2.startDate)"
									label="Start Date"
									outlined
									dense
									class="rounded-input"
									readonly
									v-bind="attrs"
									v-on="on"
								></v-text-field>
							</template>
							<v-date-picker
								v-model="period2.startDate"
								@input="period2.menu1 = false"
							></v-date-picker>
						</v-menu>
					</v-col>

					<!-- period 2 end date -->
					<v-col cols="12" md="6" sm="6">
						<v-menu
							v-model="period2.menu2"
							:close-on-content-click="false"
							:nudge-right="40"
							transition="scale-transition"
							offset-y
							min-width="auto"
						>
							<template v-slot:activator="{ on, attrs }">
								<v-text-field
									:value="formattedDate(period2.endDate)"
									label="End Date"
									outlined
									dense
									class="rounded-input"
									readonly
									v-bind="attrs"
									v-on="on"
								></v-text-field>
							</template>
							<v-date-picker
								v-model="period2.endDate"
								@input="period2.menu2 = false"
							>
							</v-date-picker>
						</v-menu>
					</v-col>
				</v-row>
			</v-col>
		</v-row>

		<v-row justify="center">
			<v-col>
				<v-btn color="primary" @click="getCalculatedData();handleTourAfterClick();">
					Calculate
				</v-btn>
			</v-col>
		</v-row>

		<v-row justify="center">
			<v-col cols="12" md="8">
				<v-card class="mb-4 mt-2" v-if="makeVisible">
					<!-- data table -->
					<v-simple-table v-if="!isLoading">
						<template v-slot:default>
							<thead>
								<tr>
									<th class="text-center">
										Category Name
									</th>
									<th class="text-center">
										{{ formattedDate(period1.startDate) + ' - ' + formattedDate(period1.endDate) }} (₹)
									</th>
									<th class="text-center">
										{{ formattedDate(period2.startDate) + ' - ' + formattedDate(period2.endDate) }} (₹)
									</th>
								</tr>
							</thead>
							<tbody>
								<!-- income heading row -->
								<tr v-if="compareItems.income_categories && compareItems.income_categories.length > 0" class="income">
									<td>
										<b> Total Incomes </b>
									</td>
									<td>
										<b> {{ totals.period1Income }} </b>
									</td>
									<td>
										<b> {{ totals.period2Income }} </b>
									</td>
								</tr>
								<tr v-else>
									<td>
										<b> Total Incomes </b>
									</td>
									<td>
										<b> - </b>
									</td>
									<td>
										<b> - </b>
									</td>
								</tr>
								<!-- income compareItems (if available) -->
								<tr
									v-for="item in compareItems.income_categories"
									:key="item.id"
								>
									<td>{{ item.title }}</td>
									<td>{{ item.first_total_amount ? item.first_total_amount : '-' }}</td>
									<td>{{ item.second_total_amount ? item.second_total_amount : '-' }}</td>
								</tr>

								<!-- expenses heading row -->
								<tr v-if="compareItems.expense_categories && compareItems.expense_categories.length > 0" class="expense">
									<td>
										<b> Total Expenses </b>
									</td>
									<td>
										<b> {{ totals.period1Expense }} </b>
									</td>
									<td>
										<b> {{ totals.period2Expense }} </b>
									</td>
								</tr>
								<tr v-else class="expense">
									<td>
										<b> Total expenses </b>
									</td>
									<td>
										<b> - </b>
									</td>
									<td>
										<b> - </b>
									</td>
								</tr>
								<!-- expense items (if available) -->
								<tr
									v-for="item in compareItems.expense_categories"
									:key="item.id"
								>
									<td>{{ item.title }}</td>
									<td>{{ item.first_total_amount ? item.first_total_amount : '-' }}</td>
									<td>{{ item.second_total_amount ? item.second_total_amount : '-' }}</td>
								</tr>
								<tr>
									<td>
										<b> Grand Total </b>
									</td>
									<td 
										:class="totals.period1GrandTotal - totals.period2GrandTotal >= 0
												? 'income font-weight-bold' : 'expense font-weight-bold'"
									>
										{{ Math.round(totals.period1GrandTotal*100)/100 }}
									</td>
									<td 
										:class="totals.period2GrandTotal - totals.period1GrandTotal >= 0
												? 'income font-weight-bold' : 'expense font-weight-bold'"
									>
										{{ Math.round(totals.period2GrandTotal*100)/100 }}
									</td>
								</tr>
							</tbody>
						</template>
					</v-simple-table>
				</v-card>
			</v-col>
		</v-row>

		<!-- snackbar -->
		<v-snackbar v-model="snackbar.visible" :color="snackbar.color" multi-line>
			{{ snackbar.message }}
			<template v-slot:action="{ attrs }">
				<v-btn dark text v-bind="attrs" @click="snackbar.visible = false"
					>Close</v-btn
				>
			</template>
		</v-snackbar>
	</div>
</template>

<script>
import moment from "moment";
import api from "@api/index";
import { mapActions, mapGetters } from "vuex";
import endpoints from "@api/repository";
import Helper from "@utils/misc.js";
import JsonExcel from "vue-json-excel";
import TourHelper from "@utils/tour_helper";


export default {
	name: "Compare",

	components: {
		downloadExcel: JsonExcel,
	},

	data() {
		return {
			moment: moment,
			snackbar: {
				visible: false,
				message: "",
				color: "dark",
			},

			makeVisible: false,

			period1: {
				menu1: false,
				menu2: false,
				startDate: null,
				endDate: null,
			},
			period2: {
				menu1: false,
				menu2: false,
				startDate: null,
				endDate: null,
			},

			headers: [
				{
					text: "Category Name",
					value: "category",
				},
				{
					text: "Amount(₹)",
					value: "amount1",
				},
				{
					text: "Amount(₹)",
					value: "amount2",
				},
			],

			compareItems: {},

			totals: {
				period1Income: null,
				period2Income: null,
				period1Expense: null,
				period2Expense: null,
				period1GrandTotal: null,
				period2GrandTotal: null
			},

			export_in_progress: false,
		};
	},

	computed: {
		...mapGetters(["currentUser", "accessToken", "isLoading"]),

		apiEssentials() {
			return {
				accessToken: this.accessToken,
				setLoading: this.setLoading,
				handleErrorsFunction: this.handleApiErrors,
			};
		},

		
	},

	methods: {
		...mapActions(["setLoading"]),

		handleTour() {
        var steps = [];
        steps.push({id: '1', description: "Enter your start date and end date of two intervals and click on calculate to compare transaction summary.", el: '#compare-row'});
        TourHelper.startTour(this, "compare", steps);
    },

	handleTourAfterClick() {
        var steps = [];
        steps.push({id: '1', description: "Total <b style='color:#b9f6ca;'>Incomes</b> and total <b style='color:#ffcdd2;'>Expenses</b> betweem both specified interval are shown here"});
        TourHelper.startTour(this, "compare__after__click", steps);
    },

		// handle api errors
		async handleApiErrors(err) {
			this.setLoading(false);
			console.log(err);
			if (err.response) {
				this.snackbar.message = "Unable to process request";
				this.snackbar.color = "error";
				this.snackbar.visible = true;
			}
		},

		totalSum: Helper.totalSum,

		formattedDate(date) {
			if (!date) return;

			var formatted = moment(date, "YYYY-MM-DD").format("ll");
			return formatted;
		},

		// validate if no fields are left empty
		validate() {
			// check empty dates
			if (!this.period1.startDate) {
				this.snackbar.message = "Please select Start Date for Period 1";
				this.snackbar.visible = true;
				this.snackbar.color = "error";
				return false;
			} else if (!this.period1.endDate) {
				this.snackbar.message = "Please select End Date for Period 1";
				this.snackbar.visible = true;
				this.snackbar.color = "error";
				return false;
			} else if (!this.period2.startDate) {
				this.snackbar.message = "Please select Start Date for Period 2";
				this.snackbar.visible = true;
				this.snackbar.color = "error";
				return false;
			} else if (!this.period2.endDate) {
				this.snackbar.message = "Please select End Date for Period 2";
				this.snackbar.visible = true;
				this.snackbar.color = "error";
				return false;
			}
			// check if endDate is not before startDate for period1 or 2
			else if (
						moment(this.period1.endDate, "YYYY-MM-DD").isBefore(moment(this.period1.startDate),"YYYY-MM-DD")
						||
						moment(this.period2.endDate, "YYYY-MM-DD").isBefore(moment(this.period2.startDate),"YYYY-MM-DD")
					) 
			{
				this.snackbar.message =
					"Start Date cannot be before End Date! Please select proper dates";
				this.snackbar.visible = true;
				this.snackbar.color = "error";
				return false;
			}

			return true;
		},

		// get data when calculate btn is clicked
		getCalculatedData() {
			if (this.validate()) {
				this.getCompareItems()
			}
		},

		// api call to fetch items for comparison table
		async getCompareItems() {
			var url = endpoints.financeReportsCompare;
			if(this.period1.startDate && this.period1.endDate && this.period2.startDate && this.period2.endDate) {
				url += `?first_start_date=${this.period1.startDate}&first_end_date=${this.period1.endDate}&second_start_date=${this.period2.startDate}&second_end_date=${this.period2.endDate}`
			}
			var response = await api.call(this.apiEssentials, url);
			if (response) {
				this.compareItems = response;
				this.getTotalsFromAmounts()
				this.makeVisible = true;
			}
		},

		// calculate various totals from amounts
		getTotalsFromAmounts() {
			this.totals.period1Income = this.totalSum(this.compareItems.income_categories, "first_total_amount")
			this.totals.period2Income = this.totalSum(this.compareItems.income_categories, "second_total_amount")
			this.totals.period1Expense = this.totalSum(this.compareItems.expense_categories, "first_total_amount")
			this.totals.period2Expense = this.totalSum(this.compareItems.expense_categories, "second_total_amount")

			this.totals.period1GrandTotal = this.totals.period1Income - this.totals.period1Expense
			this.totals.period2GrandTotal = this.totals.period2Income - this.totals.period2Expense
		},

		// create fields for export file
		getExportFields() {
			let header1 = this.formattedDate(this.period1.startDate) + ' - ' + this.formattedDate(this.period1.endDate)
			let header2 = this.formattedDate(this.period2.startDate) + ' - ' + this.formattedDate(this.period2.endDate)
			let temp = {
				"Title": "title",
				"Transaction Type": {
					field: "transaction_type",
					callback: (value) => {
						return value == "E" ? "Expense" : "Income";
					},
				},
			}

			temp[header1] = {
					field: "first_total_amount",
					callback: (value) => {
						return value ? value : '-'
					}
				}

			temp[header2] = {
					field: "second_total_amount",
					callback: (value) => {
						return value ? value : '-'
					}
				}

				return temp
		},

		// fetch details to export
		async getExportData() {
			this.export_in_progress = true;
			var url = endpoints.financeReportsCompare;

			if(this.period1.startDate && this.period1.endDate && this.period2.startDate && this.period2.endDate) {
				url = Helper.addUrlParams(url, ["export=true", `first_start_date=${this.period1.startDate}`, `first_end_date=${this.period1.endDate}`, `second_start_date=${this.period2.startDate}`, `second_end_date=${this.period2.endDate}`]);
			}

			var response = await api.call(this.apiEssentials, url)
			if(response) {
				this.export_in_progress = false
				return response
			}
		},
	},

	created() {
		this.handleTour();
	},
};
</script>

<style>
.income {
	background-color: #b9f6ca;
}
.expense {
	background-color: #ffcdd2;
}
</style>
